/**
 * Shared human-readable field labels for validation error messages.
 *
 * Used by:
 * - helpers.ts (addMissingPersonalRequiredErrors) for error message text
 * - StepReview.tsx for the Review & Submit validation error list
 * - Any step's ErrorSummary as a fallback label source
 */

/**
 * NP-616 (review follow-up): every field `declarationsSchema` can emit an issue
 * path for — the 15 yes/no declarations plus their follow-ups. Kept as its own
 * map, spread into FIELD_LABELS both plain (borrower) and `coBorrower_`-prefixed
 * below, so a co-borrower key can never be missing one by hand again — the first
 * pass here covered only 11 of these and Copilot's review caught the gap
 * (`coBorrower_sellerRelationship`, `coBorrower_priorityLien`, etc. fell back to
 * toTitleCase, which reads "Co Borrower_sellerRelationship").
 */
const DECLARATIONS_LABELS: Record<string, string> = {
  primaryResidence: 'Primary Residence',
  priorOwnership: 'Prior Property Ownership',
  priorUsage: 'Prior Property Usage',
  priorTitle: 'Prior Property Title Held',
  sellerRelationship: 'Seller Relationship',
  undisclosedBorrowedFunds: 'Undisclosed Borrowed Funds',
  undisclosedAmount: 'Undisclosed Borrowed Funds Amount',
  otherMortgage: 'Other Mortgage Before Closing',
  newCredit: 'New Credit Before Closing',
  priorityLien: 'Priority Lien',
  coMaker: 'Co-Maker or Endorser',
  outstandingJudgments: 'Outstanding Judgments',
  deedInLieu: 'Deed in Lieu of Foreclosure',
  deedInLieuDate: 'Deed in Lieu Date',
  federalDebtDelinquent: 'Federal Debt Delinquency',
  lawsuit: 'Pending Lawsuit',
  shortSale: 'Short Sale',
  shortSaleDate: 'Short Sale Date',
  bankruptcy: 'Bankruptcy',
  bankruptcies: 'Bankruptcy Details',
  foreclosure: 'Foreclosure',
  foreclosureDate: 'Foreclosure Date',
};

export const FIELD_LABELS: Record<string, string> = {
  // Personal Info
  firstName: 'First Name',
  middleName: 'Middle Name',
  lastName: 'Last Name',
  suffix: 'Suffix',
  dateOfBirth: 'Date of Birth',
  ssn: 'Social Security Number',
  email: 'Email Address',
  phone: 'Phone Number',
  citizenship: 'Citizenship Status',
  maritalStatus: 'Marital Status',
  militaryService: 'Military Service',
  militaryExpiration: 'Military Service Expiration',
  dependents: 'Number of Dependents',
  dependentCount: 'Number of Dependents',
  dependentAges: 'Dependent Ages',
  preferredLanguage: 'Preferred Language',

  // Residency
  address: 'Current Address',
  city: 'City',
  state: 'State',
  zip: 'ZIP Code',
  residencyYears: 'Years at Current Address',
  residencyMonths: 'Months at Current Address',
  housingType: 'Housing Type',
  monthlyRent: 'Monthly Rent/Payment',

  // Property & Loan (step-specific address key for property schema errors remapped from 'address')
  propertyAddress: 'Property Address',
  propertyCity: 'Property City',
  propertyState: 'Property State',
  propertyZip: 'Property ZIP Code',
  propertyCounty: 'Property County',
  estimatedValue: 'Estimated Value',
  // NP-413: purchasePrice / downPayment labels removed with the wizard fields.
  loanAmount: 'Loan Amount',
  loanPurpose: 'Loan Purpose',
  occupancy: 'Occupancy Type',
  propertyType: 'Property Type',
  numberOfUnits: 'Number of Units',

  // Income & Employment
  employment: 'Employment History',
  employers: 'Employers',
  employments: 'Employment History',
  borrowerEmployments: 'Borrower Employment',
  coBorrowerEmployments: 'Co-Borrower Employment',
  incomes: 'Income Sources',
  assets: 'Assets',
  employerName: 'Employer Name',
  jobTitle: 'Job Title',
  monthlyIncome: 'Monthly Income',
  yearsOnJob: 'Years on Job',

  // Declarations
  ...DECLARATIONS_LABELS,
  // NP-616: same toTitleCase-on-underscore problem the coBorrower_ demographics
  // keys below were added to avoid — without these, the Review & Submit notice
  // for a co-borrower's declaration would read "Co Borrower_primaryResidence".
  ...Object.fromEntries(
    Object.entries(DECLARATIONS_LABELS).map(([key, label]) => [
      `coBorrower_${key}`,
      `Co-Borrower: ${label}`,
    ]),
  ),

  // Demographics
  ethnicity: 'Ethnicity',
  race: 'Race',
  races: 'Race',
  sex: 'Sex',
  // NP-461 (reopened): the Review & Submit notice must name WHICH borrower still
  // owes HMDA answers. Without these the list fell back to toTitleCase, which
  // only splits on capitals and leaves the underscore alone — the borrower would
  // have read "Co Borrower_sex". A bare "Demographics" would have been worse
  // still, since the co-borrower's fields are not shown on the Review page.
  // The toTitleCase-fallback shape is pinned by a test, so re-deriving these
  // keys from camelCase can never quietly resurrect the raw form.
  coBorrower_sex: 'Co-Borrower: Sex',
  coBorrower_ethnicity: 'Co-Borrower: Ethnicity',
  coBorrower_races: 'Co-Borrower: Race',

  // Disclosures
  consentedDisclosures: 'Consented Disclosures',
  signatureName: 'Electronic Signature',
  signatureDate: 'Signature Date',

  // Co-Borrower
  coBorrower: 'Co-Borrower',
  coBorrowerSpouse: 'Co-Borrower Spouse',
  domesticRelationship: 'Domestic Relationship',
  vaSurvivingSpouse: 'VA Surviving Spouse',
};

/**
 * Converts a camelCase field name to Title Case as a fallback.
 * e.g., "maritalStatus" → "Marital Status"
 */
export function toTitleCase(fieldName: string): string {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/**
 * Returns the human-readable label for a field key.
 * Falls back to toTitleCase conversion if not in the map.
 */
export function getFieldLabel(fieldKey: string): string {
  return FIELD_LABELS[fieldKey] || toTitleCase(fieldKey);
}

/**
 * Scrolls to the first field with an error and focuses it.
 * Uses multiple selectors to find the element (id, name, data-field attribute).
 */
export function scrollToFirstError(errors: Record<string, string>): void {
  const firstField = Object.keys(errors)[0];
  if (!firstField) return;

  // Try multiple selectors to find the field element
  const fieldElement =
    document.getElementById(firstField) ||
    document.getElementById(`field-${firstField}`) ||
    document.querySelector(`[name="${firstField}"]`) ||
    document.querySelector(`[data-field="${firstField}"]`);

  if (fieldElement) {
    const headerOffset = 100; // Account for sticky header
    const elementPosition = fieldElement.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });

    // Focus the field after scroll animation completes
    setTimeout(() => {
      if (fieldElement instanceof HTMLElement) {
        fieldElement.focus();
      }
    }, 350);
  }
}
